/** @jsxImportSource @emotion/react */
"use client";

import type { Interpolation } from "@emotion/react";
import { useTranslations } from "@i18n/react";
import { HallowAnalyticsEvent, useAnalytics } from "@packages/analytics";
import { matchSelectableGuide, useCurrentQueueItem } from "@packages/media";
import { useContext, useEffect, useState } from "react";

import type { ThemeType } from "../../../../../../global/emotion";
import {
  colors,
  constantSemanticColors,
  ThemeProvider,
  zIndices,
} from "../../../../../../global/emotion";
import { IconButton } from "../../../../../components/_base/_core/IconButton/IconButton.emotion";
import { AudioControlsIcon } from "../../../../../components/_base/icons/Icon/GeneralIcons";
import { ModalDropdownMenu } from "../../../../../components/_base/modals/ModalDropdownMenu/ModalDropdownMenu.emotion";
import { ModalDropdownMenuHeader } from "../../../../../components/_base/modals/ModalDropdownMenuHeader/ModalDropdownMenuHeader.emotion";
import { ModalTabContainer } from "../../../../../components/_base/modals/ModalTabContainer/ModalTabContainer.emotion";
import { ModalTabContent } from "../../../../../components/_base/modals/ModalTabContent/ModalTabContent.emotion";
import { useIsDesktopViewport } from "../../../../../hooks/useIsDesktopViewport";
import type { WithButtonWrapper } from "../../../../../types/withButtonWrapper.types";
import { PlayerInternalContext } from "../../../core/MediaPlayer/MediaPlayer.emotion";
import { FragmentAsChild } from "../../../primitives/FragmentAsChild/FragmentAsChild";
import { SelectBackgroundTab } from "../SelectBackground/SelectBackgroundTab.emotion";
import { SelectBackgroundTabContent } from "../SelectBackground/SelectBackgroundTabContent.emotion";
import { SelectGuideTab } from "../SelectGuide/SelectGuideTab.emotion";
import { SelectGuideTabContent } from "../SelectGuide/SelectGuideTabContent.emotion";
import { SelectLengthTab } from "../SelectLength/SelectLengthTab.emotion";
import { SelectLengthTabContent } from "../SelectLength/SelectLengthTabContent.emotion";
import { SelectPlaybackSpeedTab } from "../SelectPlaybackSpeed/SelectPlaybackSpeedTab.emotion";
import { SelectPlaybackSpeedTabContent } from "../SelectPlaybackSpeed/SelectPlaybackSpeedTabContent.emotion";
import { SelectTimerTab } from "../SelectTimer/SelectTimerTab.emotion";
import { SelectTimerTabContent } from "../SelectTimer/SelectTimerTabContent.emotion";

const darkModeStyle: Interpolation<ThemeType> = (theme) => ({
  color: theme.semanticColors.primary,
});
const containerStyle: Interpolation = {
  background: colors.transparent,
};

const defaultOverlayStyle: Interpolation = {
  zIndex: zIndices.overlayOverMediaPlayer,
};

const defaultZAxisStyle: Interpolation = {
  zIndex: zIndices.modalOverMediaPlayer,
};

export type MediaMenuProps = WithButtonWrapper<{ showControls?: boolean }>;

export type MediaMenuTabProps = { disabled?: boolean };

export const MediaMenu = ({
  Wrapper = FragmentAsChild,
  showControls = true,
}: MediaMenuProps) => {
  const t = useTranslations();
  const { currentItem } = useCurrentQueueItem();
  const analytics = useAnalytics();
  const { popupRef } = useContext(PlayerInternalContext);
  const [open, setOpen] = useState<boolean>(false);
  const isDesktop = useIsDesktopViewport();

  useEffect(() => {
    if (!showControls && isDesktop && open) {
      setOpen(false);
    }
  }, [showControls, isDesktop, open]);

  if (!currentItem)
    return (
      <IconButton
        key={"mediaSettingsButton"}
        icon={
          <AudioControlsIcon fill={constantSemanticColors.onColorVeryHigh} />
        }
        disabled={true}
        variant="transparentWhite"
      />
    );

  const { bg_sounds_enabled, speed_changes_enabled, guide_ids } =
    currentItem?.selected_audio ?? {};
  const isRadio = currentItem?.type === "radio_song";
  const hasGuides =
    currentItem?.type === "prayer" &&
    currentItem.prayer.selectable_guides.length > 1;
  let hasLengths = false;
  if (currentItem?.type === "prayer") {
    const selectedGuides = currentItem.prayer.selectable_guides.find((sg) =>
      matchSelectableGuide(sg.guide_ids, guide_ids),
    );
    hasLengths = selectedGuides.tracks.length > 1;
  }

  const contentCSS = [darkModeStyle, defaultZAxisStyle];

  return (
    <ThemeProvider forceTheme="dark" css={containerStyle}>
      <ModalDropdownMenu
        trigger={
          <Wrapper asChild>
            <IconButton
              key={"mediaSettingsButton"}
              icon={
                <AudioControlsIcon
                  fill={constantSemanticColors.onColorVeryHigh}
                />
              }
              variant="transparentWhite"
              onClickCapture={() =>
                analytics.track({
                  event: HallowAnalyticsEvent.TappedTrackSettings,
                })
              }
              tabIndex={0}
            />
          </Wrapper>
        }
        dropdownMenuProps={{
          contentCSS: contentCSS,
          portalProps: { container: popupRef.current },
        }}
        modalProps={{
          contentCSS: contentCSS,
          overlayCSS: defaultOverlayStyle,
          portalProps: { container: popupRef.current },
        }}
        open={open}
        onOpenChange={setOpen}
      >
        <ModalTabContainer defaultValue={"menu"}>
          <ModalTabContent
            value={"menu"}
            header={
              <ModalDropdownMenuHeader>
                {t("settings_title")}
              </ModalDropdownMenuHeader>
            }
          >
            <SelectBackgroundTab disabled={!bg_sounds_enabled || isRadio} />
            <SelectGuideTab disabled={!hasGuides || isRadio} />
            <SelectLengthTab disabled={!hasLengths || isRadio} />
            <SelectPlaybackSpeedTab
              disabled={!speed_changes_enabled || isRadio}
            />
            <SelectTimerTab />
          </ModalTabContent>
          <SelectBackgroundTabContent />
          <SelectGuideTabContent />
          <SelectLengthTabContent />
          <SelectPlaybackSpeedTabContent />
          <SelectTimerTabContent />
        </ModalTabContainer>
      </ModalDropdownMenu>
    </ThemeProvider>
  );
};
